// ──────────────────────────────────────────────
// Recording Service Types
// See ADR-006 for design rationale
// ──────────────────────────────────────────────

import type { StorageService } from '../storage';
import type { UploadService } from '../upload';

// ── Recording Status ──

export type RecordingServiceStatus = 'idle' | 'recording' | 'paused' | 'stopping' | 'error';

// ── State (exposed via useSyncExternalStore) ──

export interface RecordingState {
  status: RecordingServiceStatus;
  /** Elapsed recording time in seconds */
  duration: number;
  /** Number of finalized chunks (uploaded to UploadService) */
  chunkCount: number;
  /** Device ID from the active MediaStream track */
  activeDeviceId: string | null;
  /** Actual negotiated MIME type (e.g. 'audio/webm;codecs=opus') */
  mimeType: string | null;
  /** True after the init segment has been extracted from chunk 0 */
  initSegmentCaptured: boolean;
  /** Last error message, if any */
  error: string | null;
}

// ── Events ──

export interface RecordingEventMap {
  statusChanged: { status: RecordingServiceStatus; prev: RecordingServiceStatus };
  chunkReady: {
    sessionId: string;
    chunkIndex: number;
    chunkId: string;
    contentType: string;
    byteSize: number;
  };
  chunkError: { chunkId: string; error: string };
  initSegmentCaptured: { mimeType: string; byteSize: number };
  error: { message: string; cause?: unknown };
  audioLevel: { level: number }; // 0-255 raw frequency average (matches WebRecordingService scale)
}

export type RecordingEventHandler<K extends keyof RecordingEventMap> = (
  data: RecordingEventMap[K],
) => void;

// ── Configuration ──

export interface RecordingServiceConfig {
  storageService: StorageService;
  uploadService: UploadService;
  /** MediaRecorder timeslice in ms. Default: 90_000 */
  timesliceMs?: number;
  /** Interval for requestData() crash-recovery flushes in ms. Default: 2_000 */
  requestDataIntervalMs?: number;
  /** Maximum chunk size in bytes before routing to multipart upload. Default: 5MB */
  maxChunkBytes?: number;
}

// ── Constants ──

/** Chunk index used to persist the init segment in StorageService */
export const INIT_SEGMENT_CHUNK_INDEX = -1;

/** Chunk index used for the staging (crash-recovery) write */
export const STAGING_CHUNK_INDEX = -2;

/** Default timeslice for MediaRecorder (90s upload boundary) */
export const DEFAULT_TIMESLICE_MS = 90_000;

/** Default crash-recovery flush interval */
export const DEFAULT_REQUEST_DATA_INTERVAL_MS = 2_000;

/** Default max chunk size before multipart routing (5MB) */
export const DEFAULT_MAX_CHUNK_BYTES = 5 * 1024 * 1024;

/** Web Lock name for cross-tab recording exclusion */
export const RECORDING_LOCK_NAME = 'jotpsych-recording';

// ── MIME Type Detection ──

const MIME_TYPE_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/ogg',
] as const;

/**
 * Detect the best supported MIME type for MediaRecorder.
 * Returns undefined if no supported type found (shouldn't happen in modern browsers).
 */
export function getSupportedMimeType(): string | undefined {
  return MIME_TYPE_CANDIDATES.find((t) => MediaRecorder.isTypeSupported(t));
}
